import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';

import { Button, Chip, CurrenciesUSD, Icon, Table, TableColumn, TableVariant, Tooltip } from '@column/column-ui-kit';

import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { AchBatchRepository } from '~/repositories/Transfer/AchBatchRepository';
import { useSessionStore } from '~/stores/Session';
import { ACHTransferBatchRecord } from '~/typings/API';

import { RecordDetailsModal } from './RecordDetailsModal';
import { RECORD_STATUS_META, transferUrlFor } from './recordHelpers';

const StackedCellWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  padding: 4px 16px;
  height: 40px;
  width: 100%;
  box-sizing: border-box;
`;

const StackedPrimary = styled.span`
  font-size: 14px;
  line-height: 18px;
  color: ${({ theme }) => theme.foreground};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StackedNote = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StackedCell: React.FC<{ primary: React.ReactNode; note?: string | null }> = ({ primary, note }) => (
  <StackedCellWrapper>
    <StackedPrimary>{primary}</StackedPrimary>
    {note ? <StackedNote>{note}</StackedNote> : null}
  </StackedCellWrapper>
);

const POLL_MS = 5000;

const POLLING_BATCH_STATUSES = new Set(['validating', 'processing']);

const TYPE_LABEL: Record<string, string> = {
  credit: 'Credit',
  debit: 'Debit',
};

const columns: TableColumn<ACHTransferBatchRecord>[] = [
  {
    header: 'Amount / Description',
    accessor: 'amount',
    width: { min: 180, max: '3fr' },
    cell: ({ row, value }) => (
      <Table.AmountCell
        currencyList={CurrenciesUSD}
        fixedCurrencyCode={row.currency}
        value={value}
        note={row.description || undefined}
        isBold
        isColorized
      />
    ),
  },
  {
    header: 'Type',
    accessor: 'type',
    width: { min: 100, max: '1fr' },
    cell: ({ value }) => <Table.ChipCell value={TYPE_LABEL[value ?? ''] ?? value ?? '-'} variant="subtle" />,
  },
  {
    header: 'Receiver',
    accessor: 'receiverName',
    width: { min: 160, max: '2fr' },
    cell: ({ row, value }) => <StackedCell primary={value || '-'} note={row.companyName || null} />,
  },
  {
    header: 'Status',
    accessor: 'status',
    width: { min: 130, max: '1fr' },
    cell: ({ value }) => {
      const meta = RECORD_STATUS_META[value] ?? { type: 'default' as const, label: value };
      return (
        <Table.Cell>
          <Chip variant="subtle" type={meta.type}>
            {meta.label}
          </Chip>
        </Table.Cell>
      );
    },
  },
  {
    header: 'Error',
    accessor: 'error',
    width: { min: 180, max: '3fr' },
    cell: ({ row }) =>
      row.error ? (
        <Tooltip content={row.error.message} placement="top">
          <StackedCell primary={row.error.message} note={row.error.code} />
        </Tooltip>
      ) : (
        <Table.Cell>—</Table.Cell>
      ),
  },
];

const STATUS_FILTER_OPTIONS: { label: string; value: string }[] = [
  { label: 'All', value: '' },
  ...Object.entries(RECORD_STATUS_META).map(([value, { label }]) => ({ label, value })),
];

interface AchBatchRecordsTableProps {
  batchId: string;
  batchStatus: string;
  statusFilter: string;
}

export const AchBatchRecordsTable: React.FC<AchBatchRecordsTableProps> = ({ batchId, batchStatus, statusFilter }) => {
  const platformId = useSessionStore((s) => s.currentPlatform?.id);
  const isSandbox = useSessionStore((s) => s.isSandbox);
  const navigate = useNavigate();
  const { cursor, onNext, setPageSize, reset, ...pagination } = usePaginationState();

  const [prevFilter, setPrevFilter] = useState(statusFilter);
  let effectiveCursor: string | undefined = cursor;
  if (prevFilter !== statusFilter) {
    setPrevFilter(statusFilter);
    reset();
    effectiveCursor = undefined;
  }

  const { data, isLoading } = useQuery({
    queryKey: [
      'ach-batch-records',
      platformId,
      isSandbox,
      batchId,
      effectiveCursor,
      pagination.pageSize,
      statusFilter,
      batchStatus,
    ],
    queryFn: () =>
      AchBatchRepository.getRecords(batchId, {
        limit: pagination.pageSize,
        startingAfter: effectiveCursor,
        status: statusFilter || undefined,
      }),
    enabled: !!batchId,
    refetchInterval: () => (POLLING_BATCH_STATUSES.has(batchStatus) ? POLL_MS : false),
  });

  const records = data?.records ?? [];

  // Force remount once data arrives so ui-kit measures the action column with body cells.
  const tableMountKey = data === undefined ? 'pre' : 'ready';

  const rowAction = useCallback(
    (row: ACHTransferBatchRecord) => {
      const url = transferUrlFor(row);
      return url ? (
        <Button
          onClick={(event: React.SyntheticEvent) => {
            event.stopPropagation();
            navigate(url);
          }}
          icon={<Icon.ChevronRightLarge />}
          iconRight
          size="tiny"
          variant="secondary"
        >
          View
        </Button>
      ) : null;
    },
    [navigate]
  );

  const [openRecordId, setOpenRecordId] = useState<string | null>(null);
  const openRecord = openRecordId ? (records.find((r) => r.id === openRecordId) ?? null) : null;

  return (
    <>
      <Table<ACHTransferBatchRecord>
        key={tableMountKey}
        columns={columns}
        data={records}
        pagination={{
          hasNext: data?.hasMore ?? false,
          onNext: () => (records.length ? onNext({ cursor: records[records.length - 1].id }) : undefined),
          onPageSizeChange: setPageSize,
          ...pagination,
        }}
        isLoading={isLoading}
        rowAction={rowAction}
        variant={TableVariant.List}
        onRowClick={(row) => setOpenRecordId(row.id)}
      />
      {openRecord && <RecordDetailsModal record={openRecord} isOpen onClose={() => setOpenRecordId(null)} />}
    </>
  );
};

export { STATUS_FILTER_OPTIONS };
